import { createTagTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Tag Component Configuration
 */
export function createTag(s: SemanticTokens) {
  const tagTokens = createTagTokens(s);
  return {
    root: {
      fontSize: tagTokens.base.fontSize,
      fontWeight: tagTokens.base.fontWeight,
      padding: tagTokens.base.paddingY,
      gap: tagTokens.base.gap,
      borderRadius: tagTokens.base.radius,
      roundedBorderRadius: tagTokens.base.radius,
    },
    icon: { size: tagTokens.base.iconSize },
    primary: {
      background: tagTokens.brand.bg,
      color: tagTokens.brand.fg,
    },
    secondary: {
      background: tagTokens.secondary.bg,
      color: tagTokens.secondary.fg,
    },
    success: {
      background: tagTokens.status.success.bg,
      color: tagTokens.status.success.fg,
    },
    info: {
      background: tagTokens.status.neutralInfo.bg,
      color: tagTokens.status.neutralInfo.fg,
    },
    warn: {
      background: tagTokens.status.warning.bg,
      color: tagTokens.status.warning.fg,
    },
    danger: {
      background: tagTokens.status.danger.bg,
      color: tagTokens.status.danger.fg,
    },
    colorScheme: {
      light: {
        primary: { background: tagTokens.brand.bg, color: tagTokens.brand.fg },
        secondary: { background: tagTokens.secondary.bg, color: tagTokens.secondary.fg },
        success: { background: tagTokens.status.success.bg, color: tagTokens.status.success.fg },
        info: {
          background: tagTokens.status.neutralInfo.bg,
          color: tagTokens.status.neutralInfo.fg,
        },
        warn: { background: tagTokens.status.warning.bg, color: tagTokens.status.warning.fg },
        danger: { background: tagTokens.status.danger.bg, color: tagTokens.status.danger.fg },
      },
    },
    css: () => `
    .p-tag {
      font-family: ${tagTokens.base.fontFamily};
      display: inline-flex;
      align-items: center;
      justify-content: center;
      white-space: wrap;
      text-align: center;
      /* min-height keeps the 24px single-line box, while a wrapped label grows
         the tag instead of overflowing the fixed height and losing its padding. */
      min-height: ${tagTokens.base.height}; /* 24px */
      padding-block: 0.25rem;
    }
    
    .p-tag-label {
      line-height: ${tagTokens.base.lineHeight};
    }
    
    .p-tag-icon {
      width: ${tagTokens.base.iconSize};
      height: ${tagTokens.base.iconSize};
      font-size: ${tagTokens.base.iconSize};
    }
    
    /* Emphasis variant - larger text (16px vs 12px), icon stays 16px */
    p-tag[pTagEmphasis] .p-tag {
      font-size: ${tagTokens.emphasis.fontSize};
    }

    p-tag[pTagEmphasis] .p-tag-label {
      font-size: ${tagTokens.emphasis.fontSize};
      line-height: ${tagTokens.emphasis.lineHeight};
      font-weight: ${tagTokens.emphasis.fontWeight};
    }

    /* Compact variant - shorter box for dense surfaces (a calendar night, a table cell).
       The base 24px height is too tall to sit inside one, which pushed callers into
       overriding font-size and padding by hand and drifting apart. */
    p-tag[pTagCompact] .p-tag {
      height: ${tagTokens.compact.height};
      padding-inline: ${tagTokens.compact.paddingX};
      /* A compact tag sits on surfaces that can be tinted the same as its own fill — a greyed-out
         calendar night is exactly the neutral fill — so it lifts onto the ground colour and carries
         a hairline. That way it reads as a chip on a white cell and on a grey one alike. */
      background: ${s.surface.ground};
      border: 1px solid ${s.border.default};
    }

    p-tag[pTagCompact] .p-tag-label {
      font-size: ${tagTokens.compact.fontSize};
      line-height: ${tagTokens.compact.lineHeight};
    }

    /* Dashed variant - an absent/closed state, drawn as an outline in the severity's own
       colour rather than a filled tag. */
    p-tag[pTagDashed] .p-tag {
      background: transparent;
      border: 1px dashed currentColor;
    }
  `,
  };
}
export const tag = createTag(SEMANTIC);
